import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Code2,
  Eye,
  Loader2,
  RefreshCw,
  Send,
  Settings,
  Square,
  Terminal,
  Wand2,
  Activity,
  Layout,
  Download,
  Share2,
  Copy,
  ChevronDown,
  Monitor,
  MoreHorizontal,
  X,
} from 'lucide-react';
import DashboardShell from '../components/DashboardShell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import PreviewPane from '../components/app-builder/PreviewPane';
import CodeEditor from '../components/app-builder/CodeEditor';
import FileTree from '../components/app-builder/FileTree';
import PagesBrandPanel from '../components/app-builder/PagesBrandPanel';
import AppBuilderSettingsPanel from '../components/app-builder/AppBuilderSettingsPanel';
import AppBuilderModelPicker from '../components/app-builder/AppBuilderModelPicker';
import { pickDefaultBuilderModel } from '../data/builderModels';
import { formatApiDetail, parseResponseJson } from '../lib/http';
import BuildStepTracker, {
  APP_BUILD_STEPS,
  INITIAL_BUILD_PROGRESS,
  advanceBuildProgress,
  progressFromProjectState,
} from '../components/app-builder/BuildStepTracker';
import PhasePlanTracker from '../components/app-builder/PhasePlanTracker';
import BrowserQAPanel from '../components/app-builder/BrowserQAPanel';
import ProjectSelector from '../components/app-builder/ProjectSelector';
import ActiveAgentBar from '../components/app-builder/ActiveAgentBar';
import { activePipelineAgent, PIPELINE_AGENT_TITLES } from '../lib/pipelineAgents';
import { chatMessageMeta, activityLabel, formatChatTimestamp, parseThinkingContent } from '../lib/chatMessageMeta';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { useSnagcartSettings } from '../hooks/useSnagcartSettings';
import { buildPreviewFromFiles, deriveLivePreview, isCatalogHtml, isScaffoldIndexHtml, isStaleReactPreviewHtml, isViteReactScaffold, mergeProjectPreview, normalizePreviewUrl, resolvePreviewDocument, shouldUseSandpack } from '../utils/previewHtml';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { chatWithBuilder, createProject as paiCreateProject, modifyProject as paiModifyProject, deployProject as paiDeployProject } from '../services/pydanticAiBuilder';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const DEFAULT_SESSION_PROJECT_KEY = 'snagcart_app_builder_project_id';

const BUILDER_LAB_CONFIGS = {
  'lovable-clone': {
    sessionKey: 'lovable_clone_lab_project_id',
    title: 'Lovable Clone Lab',
    subtitle: 'Type a prompt on the left — your app preview appears live on the right.',
    testId: 'lovable-clone-lab',
    panelSaveId: 'lovable-clone-lab-builder',
  },
};

function sanitizeChatContent(text) {
  if (!text || typeof text !== 'string') return '';
  let cleaned = text
    .replace(/<openlovable-write[\s\S]*?<\/openlovable-write>/gi, '')
    .replace(/<openlovable-delete[^>]*\/?>/gi, '')
    .replace(/<openlovable-rename[^>]*\/?>/gi, '')
    .replace(/<openlovable-add-dependency[\s\S]*?(?:\/>|<\/openlovable-add-dependency>)/gi, '')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (!cleaned && text.length > 80) {
    return 'Updated project files — open the Files tab to view code.';
  }
  return cleaned;
}

function inferAppTypeFromPrompt(prompt) {
  const text = (prompt || '').toLowerCase();
  if (/amazon|ebay|etsy|shopify|walmart|alibaba|marketplace|(?:website|site|store)\s+like\s+\w+/.test(text)) {
    return 'ecommerce';
  }
  if (/e-?commerce|online store|storefront|woocommerce|merchandise|retail|add to cart|checkout|shopping/.test(text)) {
    return 'ecommerce';
  }
  if (/portfolio|case study|creative agency|photography/.test(text)) return 'portfolio';
  if (/coffee|caf[eé]|bakery|bistro|restaurant|tea shop|barista/.test(text)) return 'service';
  if (/restaurant|booking|reservation|clinic|appointment|salon/.test(text)) return 'service';
  if (/dashboard|analytics|crm|admin panel/.test(text)) return 'dashboard';
  if (/mobile app|ios app|android app/.test(text)) return 'mobile';
  return 'adaptive';
}

const deriveProjectNameFromPrompt = (prompt) => {
  const text = (prompt || '').toLowerCase();
  const cloneMatch = text.match(/(?:website|site|store|app|platform)\s+like\s+([a-z0-9][\w.-]{1,24})/i)
    || text.match(/similar\s+to\s+([a-z0-9][\w.-]{1,24})/i);
  if (cloneMatch) {
    const site = cloneMatch[1].replace(/\.com$/i, '').replace(/\./g, ' ');
    const label = site.charAt(0).toUpperCase() + site.slice(1);
    return `${label} Style Marketplace`;
  }
  if (/\bamazon\b/.test(text)) return 'Amazon Style Marketplace';
  if (/\bebay\b/.test(text)) return 'Ebay Style Marketplace';
  if (/\bshopify\b/.test(text)) return 'Shopify Style Store';

  const forMatch = text.match(/(?:for|about)\s+(?:my|our|a|the)\s+(.+?)(?:\.|$)/i)
    || text.match(/(?:for|about)\s+(.+?)(?:\.|$)/i);
  if (forMatch) {
    const subject = forMatch[1].trim().replace(/\s+landing\s+page.*$/i, '').trim();
    if (subject.length >= 3 && subject.length <= 60) {
      return subject.split(/\s+/).map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
    }
  }

  const cleaned = (prompt || '')
    .replace(/[^a-zA-Z0-9\s-]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 4)
    .join(' ');

  if (!cleaned) return 'New Project';
  return cleaned
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const cn = (...classes) => classes.filter(Boolean).join(' ');

const FULL_STACK_PROMPT_HINT =
  'Include a FastAPI backend with REST API, database persistence, and auth where needed.';

function isLandingPagePrompt(text) {
  return /\b(landing\s*page|marketing\s*site|brochure\s*site|single[\s-]page\s*site|promo\s*page|company\s*homepage)\b/i.test(text || '');
}

function readBuilderPrefs(settings) {
  const core = settings?.core || {};
  const services = settings?.services || {};
  return {
    autonomous: core.autonomous_mode !== false,
    fullStack: core.full_stack_mode !== false,
    fastMvp: Boolean(core.fast_mvp_mode),
    autoDeploy: Boolean(services.auto_deploy?.enabled),
  };
}

function enrichPromptForFullStack(prompt, fullStack) {
  const text = (prompt || '').trim();
  if (!fullStack || !text || isLandingPagePrompt(text)) return text;
  if (/full[- ]?stack|fastapi|backend api|rest api/i.test(text)) return text;
  return `${text}\n\n${FULL_STACK_PROMPT_HINT}`;
}

function getToken() {
  return localStorage.getItem('token') || '';
}

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getToken()}`,
  };
}

function deriveFiles(project) {
  return project?.files || {};
}

function derivePreview(project) {
  return deriveLivePreview(project);
}

const ACTIVE_BUILD_STATES = ['planning', 'generating', 'testing', 'deploying'];
const AWAITING_USER_STATES = ['awaiting_clarification', 'awaiting_requirements'];
const TERMINAL_BUILD_STATES = ['live', 'idle', 'error', 'done', 'needs_human_review'];

function isTerminalBuildState(state) {
  return TERMINAL_BUILD_STATES.includes(state);
}
function shouldKeepGenerating(state) {
  if (!state || AWAITING_USER_STATES.includes(state) || isTerminalBuildState(state)) return false;
  return ACTIVE_BUILD_STATES.includes(state);
}
function isBuildBlockingChat(project, previewHtml) {
  if (!project) return false;
  const hasUsablePreview = Boolean(previewHtml)
    || Boolean(project.preview_html)
    || Object.keys(project.files || {}).length > 0;
  // Fast-feedback UX: once a preview/skeleton exists, the user can keep
  // interacting while backend verification, design review, and QA finish.
  if (hasUsablePreview) return false;
  return ACTIVE_BUILD_STATES.includes(project.state);
}

function deriveLogs(project) {
  return project?.metadata?.logs || [];
}

function deriveChat(project) {
  const messages = project?.metadata?.chat_messages || [];
  return messages.filter((message) => {
    if (message?.mode === 'needs_human_review') return false;
    if (Array.isArray(message?.human_options) && message.human_options.length) return false;
    const text = String(message?.content || message?.text || '');
    if (/pick one of the options below/i.test(text)) return false;
    if (/still need your review/i.test(text) && /human eye/i.test(text)) return false;
    return true;
  });
}

function formatStatusLabel(state) {
  if (!state) return 'Idle';
  return state.replace(/_/g, ' ');
}

const SECONDARY_VIEWS = [
  { value: 'files', label: 'Files', icon: Code2 },
  { value: 'activity', label: 'Activity', icon: Terminal },
  { value: 'settings', label: 'Settings', icon: Settings },
];

export default function AppBuilder({ user, labMode = null }) {
  const labConfig = labMode ? BUILDER_LAB_CONFIGS[labMode] : null;
  const sessionProjectKey = labConfig?.sessionKey ?? DEFAULT_SESSION_PROJECT_KEY;
  const builderSettings = useSnagcartSettings();
  const builderPrefs = useMemo(
    () => readBuilderPrefs(builderSettings.settings),
    [builderSettings.settings],
  );
  const cloudKeys = useMemo(
    () => builderSettings.settings?.cloud_providers?.keys || {},
    [builderSettings.settings?.cloud_providers?.keys],
  );
  const settingsDefaultModel = builderSettings.settings?.providers?.default_text_model || 'gpt-4o-mini';
  const [selectedModel, setSelectedModel] = useState(() =>
    pickDefaultBuilderModel(cloudKeys, settingsDefaultModel),
  );
  useEffect(() => {
    setSelectedModel(pickDefaultBuilderModel(cloudKeys, settingsDefaultModel));
  }, [cloudKeys, settingsDefaultModel]);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [project, setProject] = useState(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [rightTab, setRightTab] = useState('preview');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewRefreshPulse, setPreviewRefreshPulse] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatPending, setChatPending] = useState(false);
  const [promptQueue, setPromptQueue] = useState([]);
  const [deployPending, setDeployPending] = useState(false);
  const [sharePending, setSharePending] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [buildProgress, setBuildProgress] = useState(INITIAL_BUILD_PROGRESS);
  const [previewSubTab, setPreviewSubTab] = useState('app');
  const [browserSession, setBrowserSession] = useState({
    steps: [],
    screenshot: null,
    url: '',
    active: false,
  });
  const [activeAgent, setActiveAgent] = useState(null);
  const [requirementsQuestions, setRequirementsQuestions] = useState([]);
  const [requirementsCountdown, setRequirementsCountdown] = useState(null);
  const [requirementsAnswering, setRequirementsAnswering] = useState(false);
  const [applyingPlan, setApplyingPlan] = useState(false);
  const [usePydanticAi, setUsePydanticAi] = useState(false);
  const eventSourceRef = useRef(null);
  const chatScrollRef = useRef(null);
  const previewEnsureRef = useRef(null);
  const requirementsTimerRef = useRef(null);
  const startRequirementsCountdownRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const files = useMemo(() => deriveFiles(project), [project]);
  const previewHtml = useMemo(() => derivePreview(project), [project]);
  const previewVersion = project?.preview_version || 0;
  const logs = useMemo(() => deriveLogs(project), [project]);
  const chatMessages = useMemo(() => deriveChat(project), [project]);
  const pendingPlan = project?.metadata?.pending_plan || null;
  const pipelinePhases = project?.metadata?.autonomous_agents?.length
    ? project.metadata.autonomous_agents
    : (project?.phases || []);
  const buildBlockingChat = isBuildBlockingChat(project, previewHtml);
  const agentRunning = buildBlockingChat || isGenerating || chatPending;
  const derivedActiveAgent = activeAgent
    || project?.metadata?.active_agent
    || activePipelineAgent(pipelinePhases)
    || (agentRunning ? { title: PIPELINE_AGENT_TITLES.planner, phase_key: 'planner', next_plan: 'Starting autonomous builder pipeline…' } : null);
  const fileKeys = Object.keys(files || {});
  const activeSelectedFile = selectedFile && files[selectedFile] ? selectedFile : fileKeys[0] || null;
  const selectedFileContent = activeSelectedFile ? files[activeSelectedFile] : '';

  const generationEngine = project?.metadata?.generation_mode;
  const builderEngine = project?.metadata?.builder_engine || generationEngine;
  const vitePreviewPort = project?.metadata?.openlovable_preview_port;
  const isFallbackPreviewMode = project?.metadata?.generation_mode === 'fullstack-fallback-frontend';
  const previewUrl = useMemo(() => {
    if (!projectId) return null;
    if (isFallbackPreviewMode) return null;
    const fromUrl = normalizePreviewUrl(project?.metadata?.openlovable_preview_url);
    if (fromUrl?.includes('/vite-preview')) return fromUrl;
    const reactWorkspace = shouldUseSandpack(files)
      || ['siteforge', 'openlovable-agent', 'openlovable-llm-only'].includes(project?.metadata?.generation_mode);
    if (reactWorkspace || vitePreviewPort) {
      return `${API_URL}/api/sitebrain/projects/${projectId}/vite-preview/`;
    }
    return null;
  }, [projectId, project?.metadata?.openlovable_preview_url, project?.metadata?.generation_mode, vitePreviewPort, files, isFallbackPreviewMode]);
  const siteforgeMode = project?.metadata?.siteforge_mode;
  // Internal engine names are never surfaced to users — single generic label.
  const engineBadgeLabel = isGenerating || previewHtml ? 'Execution' : null;
  const backgroundFinishing = isGenerating && previewHtml && !buildBlockingChat;

  const preferLivePreview = false;

  const projectDisplayName = project?.name || 'App';

  const projectStateLabel = buildBlockingChat
    ? 'Building…'
    : backgroundFinishing
      ? 'Preview live'
      : formatStatusLabel(project?.state);

  const projectAppType = project?.app_type || project?.metadata?.app_type || null;

  const previewIsRenderable = useMemo(() => {
    const html = (previewHtml || '').trim();
    if (html.length > 500 && !isViteReactScaffold(html) && !isStaleReactPreviewHtml(html)) return true;
    if (isFallbackPreviewMode) return Boolean(html);
    if (shouldUseSandpack(files) && fileKeys.length >= 3) return true;
    return Boolean(vitePreviewPort || project?.metadata?.openlovable_preview_url);
  }, [previewHtml, files, fileKeys.length, vitePreviewPort, project?.metadata?.openlovable_preview_url, isFallbackPreviewMode]);

  const previewStatusLabel = projectLoading
    ? 'Loading…'
    : (isGenerating || buildBlockingChat) && previewIsRenderable
      ? `Ready v${previewVersion} · polishing`
      : isGenerating || buildBlockingChat
        ? 'Building…'
        : previewIsRenderable
          ? `Ready v${previewVersion}`
          : 'No preview yet';

  const generatedAppUrls = useMemo(() => {
    const urls = project?.metadata?.delivery_urls || {};
    if (!projectId && !Object.keys(urls).length) return null;
    const absolutize = (value) => {
      if (!value) return '';
      const token = getToken();
      const appendToken = (url) => {
        if (!token || !url.includes('/api/sitebrain/projects/') || url.includes('token=')) return url;
        return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
      };
      if (/^https?:\/\//i.test(value)) return appendToken(value);
      const path = value.startsWith('/') ? value : `/${value}`;
      return appendToken(`${API_URL}${path.startsWith('/api') ? path : path}`);
    };
    return {
      status: urls.status || (project?.api_base_url ? 'live_sandbox' : 'generated_contract_pending_sandbox'),
      frontend: absolutize(urls.frontend_preview || (projectId ? `/api/sitebrain/projects/${projectId}/preview` : '')),
      backend: absolutize(urls.backend_api || (projectId ? `/api/sitebrain/projects/${projectId}/backend` : '')),
      swagger: absolutize(urls.swagger_docs || (projectId ? `/api/sitebrain/projects/${projectId}/backend/docs` : '')),
      health: absolutize(urls.health || (projectId ? `/api/sitebrain/projects/${projectId}/backend/health` : '')),
    };
  }, [project?.metadata?.delivery_urls, project?.api_base_url, projectId]);

  const copyGeneratedUrl = useCallback(async (label, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} URL copied`);
    } catch (error) {
      toast.error('Unable to copy URL');
    }
  }, []);


  const loadProjects = useCallback(() => fetch(`${API_URL}/api/sitebrain/projects`, { headers: { Authorization: `Bearer ${getToken()}` } })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      if (data?.projects) {
        setProjects(data.projects);
      }
      return data?.projects;
    })
    .catch(() => []), []);

  const persistProjectSession = useCallback((id) => {
    if (!id) return;
    localStorage.setItem(sessionProjectKey, id);
    const url = new URL(window.location.href);
    url.searchParams.set('project', id);
    window.history.replaceState(null, '', url.pathname + url.search);
  }, [sessionProjectKey]);

  const syncProjectState = useCallback((data) => {
    if (!data) return;
    setProjectId(data.id);
    persistProjectSession(data.id);
    setProject((prev) => {
      const isFreshRun = ['planning', 'generating'].includes(data.state)
        && Number(data.preview_version || 0) === 0
        && data.files != null
        && Object.keys(data.files || {}).length === 0;
      const incomingFiles = data.files;
      const hasIncomingFileKeys = incomingFiles && Object.keys(incomingFiles).length > 0;
      const mergedFiles = isFreshRun
        ? (incomingFiles || {})
        : hasIncomingFileKeys
          ? { ...(prev?.files || {}), ...incomingFiles }
          : (prev?.files || incomingFiles || {});
      const incomingVersion = Number(data.preview_version || 0);
      const currentVersion = Number(prev?.preview_version || 0);
      const filesChanged = JSON.stringify(mergedFiles) !== JSON.stringify(prev?.files || {});
      const useIncoming = isFreshRun || filesChanged || incomingVersion > currentVersion;

      const base = prev ? { ...prev, ...data } : { ...data };
      const meta = { ...(base.metadata || {}) };
      if (meta.openlovable_preview_url) {
        meta.openlovable_preview_url = normalizePreviewUrl(meta.openlovable_preview_url) || meta.openlovable_preview_url;
      }
      if (
        meta.openlovable_preview_port
        && (!meta.openlovable_preview_url || meta.openlovable_preview_url.includes('/preview-port'))
      ) {
        meta.openlovable_preview_url = `${API_URL}/api/sitebrain/projects/${data.id}/vite-preview/`;
      }
      const next = {
        ...base,
        metadata: meta,
        files: mergedFiles,
        preview_version: useIncoming ? (data.preview_version ?? prev?.preview_version) : prev?.preview_version,
      };
      if (isFreshRun) {
        next.preview_html = '';
      } else {
        const stored = typeof data.preview_html === 'string' ? data.preview_html.trim() : '';
        const derived = deriveLivePreview({
          ...next,
          preview_html: stored || next.preview_html || prev?.preview_html || '',
        }) || resolvePreviewDocument({
          ...next,
          preview_html: stored || next.preview_html || prev?.preview_html || '',
        });
        next.preview_html = derived
          || (stored && !isViteReactScaffold(stored) && !isStaleReactPreviewHtml(stored) ? stored : '')
          || prev?.preview_html
          || '';
      }
      return next;
    });
    if (data.metadata?.active_agent) {
      setActiveAgent(data.metadata.active_agent);
    }
    const livePreview = deriveLivePreview({
      ...data,
      files: data.files ?? undefined,
      preview_html: data.preview_html,
    });
    if (isBuildBlockingChat(data, livePreview)) {
      setIsGenerating(true);
    } else if (
      isTerminalBuildState(data.state)
      || AWAITING_USER_STATES.includes(data.state)
      || !shouldKeepGenerating(data.state, {
        filesCount: Object.keys(data.files || {}).length,
        hasPreview: Boolean(livePreview || data.preview_html),
      })
    ) {
      setIsGenerating(false);
    }
    const nextFiles = Object.keys(data.files || {});
    setSelectedFile((prev) => (prev && nextFiles.includes(prev) ? prev : nextFiles[0] || null));
  }, [persistProjectSession]);

  const appendStreamChatEntry = useCallback((entry, { logKind } = {}) => {
    if (!entry?.content) return;
    const stamp = entry.timestamp || new Date().toISOString();
    const entryId = entry.id || `stream-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const chatEntry = {
      id: entryId,
      role: entry.role || 'activity',
      content: entry.content,
      kind: entry.kind || logKind || 'status',
      label: entry.label || entry.agent || activityLabel(entry.kind || logKind || 'status'),
      timestamp: stamp,
      progress_pct: entry.progress_pct,
      phase_key: entry.phase_key,
      mode: entry.mode,
      path: entry.path,
      diff: entry.diff,
    };
    setProject((prev) => {
      if (!prev) return prev;
      const meta = { ...(prev.metadata || {}) };
      const chat = [...(meta.chat_messages || [])];
      if (chat.some((m) => m.id === entryId)) return prev;
      chat.push(chatEntry);
      const logs = [...(meta.logs || [])];
      if (logKind || entry.kind) {
        logs.push({
          id: entryId,
          kind: logKind || entry.kind || 'status',
          text: entry.content,
          timestamp: stamp,
        });
      }
      return { ...prev, metadata: { ...meta, chat_messages: chat, logs } };
    });
  }, []);

  const syncGeneratingFromStatus = useCallback((status) => {
    if (!status) return;
    if (shouldKeepGenerating(status.state, {
      filesCount: status.files_count || 0,
      hasPreview: status.has_preview_html,
    })) {
      setIsGenerating(true);
    } else {
      setIsGenerating(false);
    }
  }, []);

  const fetchProjectStatus = useCallback((id) => fetch(
    `${API_URL}/api/sitebrain/projects/${id}/status`,
    { headers: { Authorization: `Bearer ${getToken()}` } },
  )
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null), []);

  const ensurePreviewServer = useCallback((id) => {
    const targetId = id || projectId;
    if (!targetId) return;
    fetch(`${API_URL}/api/sitebrain/projects/${targetId}/dev-server/ensure`, {
      method: 'POST',
      headers: authHeaders(),
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data?.port) return;
        setProject((prev) => ({
          ...(prev || {}),
          metadata: {
            ...(prev?.metadata || {}),
            openlovable_preview_port: data.port,
            openlovable_preview_url: normalizePreviewUrl(data.preview_url) || prev?.metadata?.openlovable_preview_url,
          },
        }));
      })
      .catch(() => {});
  }, [projectId]);

  const loadProject = useCallback((id, { ensurePreview = false } = {}) => {
    setProjectLoading(true);
    fetch(`${API_URL}/api/sitebrain/projects/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data) return;
        syncProjectState(data);
        const isOl = data?.metadata?.generation_mode !== 'fullstack-fallback-frontend'
          && (
          data?.metadata?.generation_mode === 'openlovable-agent'
          || data?.metadata?.generation_mode === 'siteforge'
          || data?.metadata?.openlovable_preview_port
          || shouldUseSandpack(data?.files));
        if ((ensurePreview || isOl) && Object.keys(data?.files || {}).length > 0) {
          ensurePreviewServer(id);
        }
      })
      .catch(() => toast.error('Failed to load builder project'))
      .finally(() => setProjectLoading(false));
  }, [syncProjectState, ensurePreviewServer]);

  useEffect(() => {
    setBrowserSession({ steps: [], screenshot: null, url: '', active: false });
    setPreviewSubTab('app');
  }, [projectId]);

  useEffect(() => {
    loadProjects().then((list) => {
      const params = new URLSearchParams(window.location.search);
      const restoreId = params.get('project') || localStorage.getItem(sessionProjectKey);
      if (restoreId && (list || []).some((p) => p.id === restoreId)) {
        loadProject(restoreId);
      } else if (restoreId) {
        loadProject(restoreId);
      }
    });
  }, [loadProjects, loadProject, sessionProjectKey]);

  useEffect(() => {
    if (!projectId || !isGenerating) return undefined;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const token = getToken();
    const source = new EventSource(`${API_URL}/api/sitebrain/projects/${projectId}/stream?token=${encodeURIComponent(token)}`);
    eventSourceRef.current = source;

    source.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'verify' || payload.type === 'qa') {
          const status = payload.status || (payload.report?.ok ? 'passed' : 'failed');
          if (status === 'running' || status === 'passed' || status === 'failed') {
            setBuildProgress((prev) => advanceBuildProgress(prev, { type: 'build' }));
          }
        }
        if (payload.type === 'phase' && payload.project) {
          syncProjectState(payload.project);
          const runningPhase = (payload.project.phases || []).find((p) => p.status === 'running');
          if (runningPhase) {
            setActiveAgent({
              title: runningPhase.title,
              phase_key: runningPhase.key,
              next_plan: runningPhase.description || '',
            });
          }
        }
        if (payload.type === 'agent_handoff') {
          setActiveAgent({
            title: payload.agent,
            phase_key: payload.phase_key,
            next_plan: payload.next_plan || '',
            fixing: payload.fixing || '',
          });
        }
        if (payload.content && (payload.type === 'thinking' || payload.type === 'browser' || payload.type === 'command' || payload.type === 'tool' || payload.type === 'mcp' || payload.type === 'api')) {
          appendStreamChatEntry(payload, { logKind: payload.kind || payload.type });
          if (payload.agent || payload.label) {
            setActiveAgent({
              title: payload.label || payload.agent,
              phase_key: payload.phase_key,
              next_plan: payload.next_plan || '',
              fixing: payload.fixing || '',
            });
          }
          if (typeof payload.progress_pct === 'number') {
            setBuildProgress((prev) => ({
              ...prev,
              pct: payload.progress_pct,
            }));
          }
        }
        if (payload.type === 'phase' && payload.content) {
          appendStreamChatEntry(payload, { logKind: 'phase' });
          if (typeof payload.progress_pct === 'number') {
            setBuildProgress((prev) => ({
              ...prev,
              pct: payload.progress_pct,
            }));
          }
        }
        if (payload.type === 'echo' && payload.content) {
          appendStreamChatEntry({
            ...payload,
            role: 'activity',
            kind: 'phase',
            label: payload.label || 'Agent',
          }, { logKind: 'phase' });
        }
        if (payload.type === 'preview_urls' && payload.content) {
          appendStreamChatEntry({
            ...payload,
            role: 'assistant',
            mode: 'openlovable-llm-only',
            label: payload.label || 'Preview ready',
          });
        }
        if (payload.type === 'orchestrator') {
          const msg = payload.message || payload.label || `${payload.agent || 'Orchestrator'}: ${payload.status || 'update'}`;
          const entryId = `orch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          const stamp = new Date().toISOString();
          if (payload.status === 'plan') {
            setBuildProgress((prev) => advanceBuildProgress(prev, { type: 'plan' }));
          }
          if (payload.status === 'retry') {
            setBuildProgress((prev) => advanceBuildProgress(prev, { type: 'build' }));
          }
          setProject((prev) => {
            if (!prev) return prev;
            const meta = { ...(prev.metadata || {}) };
            if (payload.intent || payload.execution_plan) {
              meta.orchestrator = {
                ...(meta.orchestrator || {}),
                intent: payload.intent || meta.orchestrator?.intent,
                execution_plan: payload.execution_plan || meta.orchestrator?.execution_plan,
              };
            }
            const logs = [...(meta.logs || []), {
              id: entryId,
              kind: 'orchestrator',
              text: msg,
              timestamp: stamp,
            }];
            const chat = [...(meta.chat_messages || []), {
              id: entryId,
              role: 'activity',
              content: msg,
              kind: 'orchestrator',
              label: activityLabel('orchestrator'),
              timestamp: stamp,
            }];
            return { ...prev, metadata: { ...meta, logs, chat_messages: chat } };
          });
        }
        if (payload.type === 'autonomous_qa' && (payload.content || payload.detail)) {
          const entryId = `aqa-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          const stamp = new Date().toISOString();
          const stepDetail = payload.detail || payload.content;
          setBrowserSession((prev) => ({
            ...prev,
            active: true,
            steps: [
              ...prev.steps,
              { step: payload.step || 'qa', detail: stepDetail, timestamp: stamp },
            ].slice(-40),
          }));
          setPreviewSubTab('agent-browser');
          setRightTab('preview');
          if (payload.content) {
            setProject((prev) => {
              if (!prev) return prev;
              const meta = { ...(prev.metadata || {}) };
              const chat = [...(meta.chat_messages || []), {
                id: entryId,
                role: 'activity',
                content: payload.content,
                kind: 'browser',
                label: 'Browser QA',
                timestamp: stamp,
              }];
              const logs = [...(meta.logs || []), {
                id: entryId,
                kind: 'qa',
                text: payload.content,
                timestamp: stamp,
              }];
              return { ...prev, metadata: { ...meta, chat_messages: chat, logs } };
            });
          }
        }
        if (payload.type === 'browser_screenshot' && payload.image_b64) {
          setBrowserSession((prev) => ({
            ...prev,
            active: true,
            screenshot: payload.image_b64,
            url: payload.url || prev.url,
            steps: payload.step
              ? [...prev.steps, { step: payload.step, detail: 'Captured browser screenshot', timestamp: new Date().toISOString() }].slice(-40)
              : prev.steps,
          }));
          setPreviewSubTab('agent-browser');
          setRightTab('preview');
        }
        if (payload.type === 'file_edit') {
          const entryId = `edit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          const stamp = new Date().toISOString();
          const agentLabel = payload.label || payload.agent || 'Auto Fix';
          const msg = payload.content || `${agentLabel} updated ${payload.path} — ${payload.reason || 'updating file'}`;
          setProject((prev) => {
            if (!prev) return prev;
            const meta = { ...(prev.metadata || {}) };
            const chat = [...(meta.chat_messages || []), {
              id: entryId,
              role: 'edit',
              content: msg,
              path: payload.path,
              reason: payload.reason,
              diff: payload.diff,
              kind: 'edit',
              label: agentLabel,
              agent: agentLabel,
              timestamp: stamp,
            }];
            const logs = [...(meta.logs || []), {
              id: entryId,
              kind: 'edit',
              text: msg,
              timestamp: stamp,
            }];
            return { ...prev, metadata: { ...meta, chat_messages: chat, logs } };
          });
        }
        if (payload.type === 'verification') {
          setBuildProgress((prev) => advanceBuildProgress(prev, { type: 'build' }));
          const entryId = `verify-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          const stamp = new Date().toISOString();
          const icon = payload.ok ? '✅' : '⚠️';
          const msg = `${icon} ${payload.summary || 'Browser verification complete.'}`;
          setProject((prev) => {
            if (!prev) return prev;
            const meta = { ...(prev.metadata || {}) };
            const chat = [...(meta.chat_messages || []), {
              id: entryId,
              role: 'assistant',
              content: msg,
              kind: 'verification',
              timestamp: stamp,
            }];
            return { ...prev, metadata: { ...meta, chat_messages: chat } };
          });
        }
        if (payload.type === 'files' && payload.files) {
          setProject((prev) => mergeProjectPreview(prev || {}, { files: payload.files }));
        }
        if (payload.type === 'log' && payload.content) {
          const logText = (payload.content || '').toLowerCase();
          setBuildProgress((prev) => {
            if (logText.includes('generat') || logText.includes('writing') || logText.includes('openlovable')) {
              return advanceBuildProgress(prev, { type: 'codegen' });
            }
            if (logText.includes('preview') || logText.includes('vite') || logText.includes('build')) {
              return advanceBuildProgress(prev, { type: 'build' });
            }
            return prev;
          });
          const entryId = `log-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          const stamp = new Date().toISOString();
          setProject((prev) => {
            if (!prev) return prev;
            const meta = { ...(prev.metadata || {}) };
            const logs = [...(meta.logs || []), {
              id: entryId,
              kind: payload.kind || 'status',
              text: payload.content,
              timestamp: stamp,
            }];
            const chat = [...(meta.chat_messages || []), {
              id: entryId,
              role: 'activity',
              content: payload.content,
              kind: payload.kind || 'status',
              label: activityLabel(payload.kind || 'status'),
              timestamp: stamp,
            }];
            return { ...prev, metadata: { ...meta, logs, chat_messages: chat } };
          });
        }
        if (payload.type === 'preview' && (payload.html || payload.preview_url || payload.project)) {
          setBuildProgress((prev) => advanceBuildProgress(prev, { type: 'preview' }));
          const nextFiles = payload.project?.files || payload.files;
          setProject((prev) => {
            const base = prev || payload.project || {};
            const mergedMeta = {
              ...(base.metadata || {}),
              ...(payload.project?.metadata || {}),
            };
            if (payload.preview_url) {
              mergedMeta.openlovable_preview_url = normalizePreviewUrl(payload.preview_url) || payload.preview_url;
            }
            return mergeProjectPreview(
              {
                ...base,
                ...payload.project,
                metadata: mergedMeta,
                preview_version: payload.version ?? base.preview_version ?? payload.project?.preview_version,
                state: payload.project?.state || payload.state || base.state,
              },
              {
                ...(nextFiles ? { files: nextFiles } : {}),
                ...(payload.html ? { preview_html: payload.html } : {}),
              },
            );
          });
          const previewState = payload.project?.state || payload.state;
          if (previewState && isTerminalBuildState(previewState)) {
            setIsGenerating(false);
          }
          setPreviewRefreshPulse(true);
          setRightTab('preview');
          if (nextFiles && Object.keys(nextFiles).length > 0) {
            ensurePreviewServer(projectId);
          }
          window.clearTimeout(window.__appBuilderPreviewPulse);
          window.__appBuilderPreviewPulse = window.setTimeout(() => setPreviewRefreshPulse(false), 600);
        } else if (payload.project) {
          syncProjectState(payload.project);
        }
        if (payload.type === 'plan') {
          setChatPending(false);
          setBuildProgress((prev) => advanceBuildProgress(prev, { type: 'plan' }));
          if (payload.plan) {
            setProject((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                metadata: {
                  ...(prev.metadata || {}),
                  pending_plan: payload.plan,
                },
              };
            });
          }
        }
        if (payload.type === 'deployment') {
          setDeployPending(false);
          toast.success(payload.deployment?.status === 'pending_credentials' ? 'Deployment flow prepared' : 'Deployment flow triggered');
        }
        if (payload.type === 'requirements') {
          setIsGenerating(false);
          setChatPending(false);
          if (payload.status === 'questions' && Array.isArray(payload.questions)) {
            setRequirementsQuestions(payload.questions);
            startRequirementsCountdownRef.current?.(projectId, payload.timeout_seconds || 10);
            setProject((prev) => {
              if (!prev) return prev;
              const meta = { ...(prev.metadata || {}) };
              const chat = [...(meta.chat_messages || [])];
              chat.push({
                id: `req-${Date.now()}`,
                role: 'assistant',
                content: 'Pick a few options below — or wait for auto-continue.',
                mode: 'requirements',
                questions: payload.questions,
                timestamp: new Date().toISOString(),
              });
              return { ...prev, state: 'awaiting_requirements', metadata: { ...meta, chat_messages: chat } };
            });
          }
          if (payload.status === 'blocker' && Array.isArray(payload.questions)) {
            setRequirementsQuestions(payload.questions);
            startRequirementsCountdownRef.current?.(projectId, payload.timeout_seconds || 10);
            toast.message('Build needs a quick clarification');
          }
        }
        if (payload.type === 'clarification') {
          setIsGenerating(false);
          setChatPending(false);
          setActiveAgent(null);
          loadProject(projectId);
          toast.message('Answer the questions in chat to continue your build');
        }
        if (payload.type === 'complete') {
          setBuildProgress((prev) => advanceBuildProgress(prev, { type: 'preview' }));
          setIsGenerating(false);
          setChatPending(false);
          setDeployPending(false);
          setProject((prev) => mergeProjectPreview(prev || payload.project || {}, {
            ...(payload.project || {}),
            files: payload.files || payload.project?.files,
            preview_version: payload.preview_version ?? payload.project?.preview_version,
            state: payload.state || payload.project?.state,
          }));
          loadProjects();
          if (payload.mode === 'initial_build') {
            toast.success('Your app is ready');
            setRightTab('preview');
            ensurePreviewServer(projectId);
          }
          if (payload.mode === 'apply_plan') {
            setApplyingPlan(false);
            toast.success('Changes applied');
            setRightTab('preview');
            ensurePreviewServer(projectId);
          }
          if (payload.mode === 'plan') {
            setIsGenerating(false);
            toast.success('Modification plan ready — review and apply');
          }
        }
        if (payload.type === 'error') {
          setBuildProgress((prev) => advanceBuildProgress(prev, { type: 'error' }));
          setIsGenerating(false);
          setChatPending(false);
          setDeployPending(false);
          toast.error(payload.error || 'Something went wrong');
        }
        if (payload.type === 'done') {
          setBuildProgress((prev) => advanceBuildProgress(prev, { type: 'preview' }));
          setIsGenerating(false);
          setChatPending(false);
          setApplyingPlan(false);
          source.close();
          eventSourceRef.current = null;
          loadProject(projectId);
          ensurePreviewServer(projectId);
        }
      } catch (error) {
        console.error(error);
      }
    };

    source.onerror = () => {
      if (eventSourceRef.current !== source) return;
      fetchProjectStatus(projectId).then((status) => {
        if (!status) return;
        syncGeneratingFromStatus(status);
        if (!shouldKeepGenerating(status.state, {
          filesCount: status.files_count || 0,
          hasPreview: status.has_preview_html,
        })) {
          try { source.close(); } catch (_e) { /* noop */ }
          if (eventSourceRef.current === source) eventSourceRef.current = null;
        }
        if ((status.files_count || 0) > 0 || status.has_preview_html) {
          loadProject(projectId);
        }
      });
    };

    return () => {
      source.close();
      eventSourceRef.current = null;
    };
  }, [loadProject, loadProjects, projectId, isGenerating, syncProjectState, ensurePreviewServer, appendStreamChatEntry, fetchProjectStatus, syncGeneratingFromStatus]);

  useEffect(() => {
    if (!projectId || !isGenerating || previewHtml) return undefined;
    const pollPreview = () => {
      fetchProjectStatus(projectId)
        .then((status) => {
          if (!status) return;
          syncGeneratingFromStatus(status);
          if ((status.files_count || 0) > 0 || status.has_preview_html) {
            loadProject(projectId);
          }
        });
    };
    pollPreview();
    const timer = window.setInterval(pollPreview, 2500);
    return () => window.clearInterval(timer);
  }, [previewHtml, projectId, isGenerating, loadProject, fetchProjectStatus, syncGeneratingFromStatus]);

  useEffect(() => {
    if (!isGenerating && project?.preview_html) {
      setBuildProgress(progressFromProjectState(project, false));
    }
  }, [project?.state, project?.preview_html, isGenerating, project]);

  useEffect(() => {
    const root = chatScrollRef.current;
    if (!root) return;
    const viewport = root.querySelector('[data-radix-scroll-area-viewport]') || root;
    viewport.scrollTop = viewport.scrollHeight;
  }, [chatMessages, buildProgress]);

  const buildInitialPromptPayload = (descriptionOverride = null) => {
    const raw = (descriptionOverride || '').trim();
    const landing = isLandingPagePrompt(raw);
    const appDescription = enrichPromptForFullStack(
      raw,
      builderPrefs.fullStack && !landing,
    );
    const appName = deriveProjectNameFromPrompt(appDescription);
    const inferred = inferAppTypeFromPrompt(appDescription);
    const appType = landing
      ? 'landing'
      : (builderPrefs.fullStack && inferred === 'adaptive' ? 'webapp' : inferred);
    return {
      appName,
      appDescription,
      appType,
      fullPrompt: appDescription,
    };
  };

  const createFreshProject = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    localStorage.removeItem(sessionProjectKey);
    window.history.replaceState(null, '', window.location.pathname);
    previewEnsureRef.current = null;
    setProjectId(null);
    setProject(null);
    setIsGenerating(false);
    setActiveAgent(null);
    setSelectedFile(null);
    setRightTab('preview');
    setChatInput('');
    setChatPending(false);
    setDeployPending(false);
    setBuildProgress(INITIAL_BUILD_PROGRESS);
    setBrowserSession({ steps: [], screenshot: null, url: '', active: false });
    setRequirementsQuestions([]);
    setRequirementsCountdown(null);
    toast.message('New project — describe what you want to build');
  };

  const downloadProject = async () => {
    if (!projectId) {
      toast.error('Create a project first');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/sitebrain/projects/${projectId}/download`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!response.ok) {
        const { data: payload } = await parseResponseJson(response);
        throw new Error(formatApiDetail(payload, 'Download failed'));
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${(project?.name || 'snagcart-project').replace(/[^\w.-]+/g, '-')}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success('Project downloaded');
    } catch (error) {
      toast.error(error.message || 'Failed to download project');
    }
  };

  const deleteProject = async (id) => {
    try {
      await fetch(`${API_URL}/api/sitebrain/projects/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (id === projectId) {
        createFreshProject();
      }
      loadProjects();
      toast.success('Project deleted');
    } catch {
      toast.error('Failed to delete project');
    }
  };

  const refreshPreviewNow = async () => {
    if (!projectId) return;
    setPreviewRefreshPulse(true);
    previewEnsureRef.current = null;
    loadProject(projectId);
    window.clearTimeout(window.__appBuilderManualPulse);
    window.__appBuilderManualPulse = window.setTimeout(() => setPreviewRefreshPulse(false), 900);
  };

  const publishShareLink = async () => {
    if (!projectId) {
      toast.error('Create a project first');
      return;
    }
    try {
      setSharePending(true);
      const response = await fetch(`${API_URL}/api/sitebrain/projects/${projectId}/share`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const { data } = await parseResponseJson(response);
      if (!response.ok) {
        throw new Error(formatApiDetail(data, 'Unable to publish share link'));
      }
      setShareUrl(data.public_url || '');
      if (data.public_url && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(data.public_url);
        toast.success('Share link copied');
      } else {
        toast.success('Share link ready');
      }
    } catch (error) {
      toast.error(error.message || 'Unable to publish share link');
    } finally {
      setSharePending(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const clearRequirementsTimer = useCallback(() => {
    if (requirementsTimerRef.current) {
      window.clearInterval(requirementsTimerRef.current);
      requirementsTimerRef.current = null;
    }
  }, []);

  const sealRequirements = useCallback(async (targetId) => {
    if (!targetId) return;
    clearRequirementsTimer();
    setRequirementsCountdown(null);
    try {
      const response = await fetch(`${API_URL}/api/sitebrain/projects/${targetId}/requirements/seal`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const { data: result } = await parseResponseJson(response);
      if (!response.ok) {
        throw new Error(formatApiDetail(result, 'Failed to seal requirements'));
      }
      setRequirementsQuestions([]);
      if (result.status === 'generating') {
        setIsGenerating(true);
        setBuildProgress(advanceBuildProgress(INITIAL_BUILD_PROGRESS, { type: 'start' }));
      }
    } catch (error) {
      toast.error(error.message || 'Failed to continue build');
    }
  }, [clearRequirementsTimer]);

  const startRequirementsCountdown = useCallback((targetId, seconds = 10) => {
    clearRequirementsTimer();
    setRequirementsCountdown(seconds);
    requirementsTimerRef.current = window.setInterval(() => {
      setRequirementsCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearRequirementsTimer();
          sealRequirements(targetId);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearRequirementsTimer, sealRequirements]);
  startRequirementsCountdownRef.current = startRequirementsCountdown;

  const answerRequirement = useCallback(async (targetId, slotId, value, questionId) => {
    if (!targetId || requirementsAnswering) return;
    setRequirementsAnswering(true);
    try {
      const response = await fetch(`${API_URL}/api/sitebrain/projects/${targetId}/requirements/answer`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          answers: { [slotId]: value },
          question_ids: questionId ? [questionId] : [],
        }),
      });
      const { data: result } = await parseResponseJson(response);
      if (!response.ok) {
        throw new Error(formatApiDetail(result, 'Failed to submit answer'));
      }
      if (result.status === 'generating') {
        clearRequirementsTimer();
        setRequirementsQuestions([]);
        setRequirementsCountdown(null);
        setIsGenerating(true);
        setBuildProgress(advanceBuildProgress(INITIAL_BUILD_PROGRESS, { type: 'start' }));
      } else if (result.status === 'awaiting_user') {
        loadProject(targetId);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to submit answer');
    } finally {
      setRequirementsAnswering(false);
    }
  }, [requirementsAnswering, clearRequirementsTimer, loadProject]);

  useEffect(() => () => clearRequirementsTimer(), [clearRequirementsTimer]);

  const stopBuild = async () => {
    if (eventSourceRef.current) {
      try { eventSourceRef.current.close(); } catch (_e) { /* noop */ }
      eventSourceRef.current = null;
    }
    setIsGenerating(false);
    setChatPending(false);
    setActiveAgent(null);
    setPromptQueue([]);
    setBuildProgress(INITIAL_BUILD_PROGRESS);
    // Optimistically clear the build-blocking state so the preview pane stops
    // showing "Building…/Generating" immediately (don't wait for the next poll).
    setProject((prev) => {
      if (!prev) return prev;
      const hasOutput = (prev.files && Object.keys(prev.files).length > 0) || prev.preview_html;
      return { ...prev, state: hasOutput ? 'live' : 'idle' };
    });
    if (projectId) {
      try {
        await fetch(`${API_URL}/api/sitebrain/projects/${projectId}/stop`, {
          method: 'POST',
          headers: authHeaders(),
        });
      } catch (_e) { /* best-effort */ }
      loadProject(projectId);
    }
    toast.success('Build stopped');
  };

  // Drain the queued prompts once the current build completes.
  const queuedRunRef = useRef(false);
  useEffect(() => {
    if (isGenerating || chatPending) return;
    if (promptQueue.length === 0) return;
    if (queuedRunRef.current) return;
    queuedRunRef.current = true;
    const [next, ...rest] = promptQueue;
    setPromptQueue(rest);
    toast.message('Running next queued prompt…');
    Promise.resolve(requestPlan(next)).finally(() => {
      queuedRunRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenerating, chatPending, promptQueue]);

  const appendUserChatEntry = useCallback((text) => {
    const content = (text || '').trim();
    if (!content) return;
    setProject((prev) => {
      if (!prev) return prev;
      const meta = { ...(prev.metadata || {}) };
      const chat = [...(meta.chat_messages || [])];
      chat.push({
        id: `user-${Date.now()}`,
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
      });
      return { ...prev, metadata: { ...meta, chat_messages: chat } };
    });
  }, []);

  const requestPlan = async (overrideMessage = null) => {
    const isQueued = typeof overrideMessage === 'string';
    const sourceText = (isQueued ? overrideMessage : chatInput).trim();
    if (!sourceText) {
      toast.error(projectId ? 'Describe the change you want' : 'Describe the app you want to build');
      return;
    }
    const awaitingUserInput = AWAITING_USER_STATES.includes(project?.state);
    // Queue follow-up prompts while a build is already in flight.
    if (!isQueued && isGenerating && !awaitingUserInput) {
      setPromptQueue((q) => [...q, sourceText]);
      setChatInput('');
      toast.success('Queued — it will run after the current build finishes');
      return;
    }
    try {
      appendUserChatEntry(sourceText);
      setChatInput('');
      setBuildProgress(advanceBuildProgress(INITIAL_BUILD_PROGRESS, { type: 'start' }));
      const hasExistingPreview = Boolean(
        projectId
        && (
          previewHtml
          || project?.preview_html
          || Object.keys(project?.files || {}).length > 0
        ),
      );
      const isExplicitFullRebuild = /\b(rebuild|start over|from scratch|new app|complete redesign|full redesign|replace everything|reset)\b/i.test(sourceText);
      const isFullRebuild = hasExistingPreview && isExplicitFullRebuild;
      const isFollowUpChange = hasExistingPreview && !isFullRebuild;
      const isLanding = isLandingPagePrompt(sourceText);
      const useFullStack = builderPrefs.fullStack && !isFollowUpChange && !isLanding;
      const payloadMessage = enrichPromptForFullStack(sourceText, useFullStack);
      const usePlanFirst = false;

      setChatPending(true);
      if (!usePlanFirst) {
        setIsGenerating(true);
        setActiveAgent({
          title: PIPELINE_AGENT_TITLES.architect,
          phase_key: 'architect',
          next_plan: 'Starting build pipeline…',
        });
      } else {
        setActiveAgent({
          title: PIPELINE_AGENT_TITLES.architect,
          phase_key: 'architect',
          next_plan: 'Drafting section plan…',
        });
      }
      setRightTab('preview');

      if (projectId && !isFollowUpChange && !awaitingUserInput) {
        setProject((prev) => (prev ? {
          ...prev,
          files: {},
          preview_html: '',
          preview_version: 0,
          state: 'planning',
        } : prev));
        setPreviewRefreshPulse(true);
      }
      let targetId = projectId;

      const usePydanticBuilder = usePydanticAi && !awaitingUserInput && !isFollowUpChange && !builderPrefs.autonomous;
      if (usePydanticBuilder) {
        // --- PydanticAI Agent Route ---
        if (!targetId) {
          const prompt = sourceText;
          const { appName, appDescription, appType } = buildInitialPromptPayload(prompt);
          const created = await paiCreateProject({
            name: appName,
            description: appDescription,
            appType,
            fullStack: useFullStack,
            model: selectedModel,
          });
          targetId = created.project_id || created.id;
          setProjectId(targetId);
          setProject({ id: targetId, ...created, files: {}, preview_html: '', preview_version: 0, metadata: { logs: [] } });
        }

        // Use PydanticAI chat with SSE streaming
        chatWithBuilder({
          message: payloadMessage,
          projectId: targetId,
          model: selectedModel,
          onTextDelta: (delta, fullText) => {
            const entryId = `pai-${targetId}-stream`;
            setProject((prev) => {
              if (!prev) return prev;
              const meta = { ...(prev.metadata || {}) };
              const existingChat = [...(meta.chat_messages || [])];
              const existing = existingChat.findIndex((m) => m.id === entryId);
              const entry = {
                id: entryId,
                role: 'assistant',
                content: fullText,
                kind: 'agent',
                label: 'PydanticAI Builder',
                timestamp: new Date().toISOString(),
              };
              if (existing >= 0) {
                existingChat[existing] = entry;
              } else {
                existingChat.push(entry);
              }
              return { ...prev, metadata: { ...meta, chat_messages: existingChat } };
            });
          },
          onComplete: (content, resultProjectId) => {
            setChatPending(false);
            setChatInput('');
            const resolvedId = resultProjectId || targetId;
            if (resolvedId) setProjectId(resolvedId);
            fetchProjectStatus(resolvedId || targetId).then((status) => {
              syncGeneratingFromStatus(status);
              if ((status?.files_count || 0) > 0 || status?.has_preview_html) {
                loadProject(resolvedId || targetId);
              } else if (AWAITING_USER_STATES.includes(status?.state)) {
                loadProject(resolvedId || targetId);
              }
            });
            toast.success(isFollowUpChange ? 'Changes applied via PydanticAI' : 'Build started via PydanticAI');
          },
          onError: (err) => {
            setChatPending(false);
            setIsGenerating(false);
            setActiveAgent(null);
            toast.error(err.message || 'PydanticAI agent error');
          },
        });
        setChatPending(false);
        setChatInput('');
        toast.success(projectId ? 'Building with PydanticAI agent…' : 'Starting PydanticAI build…');
      } else {
        // --- Classic SiteBrain Route ---
        if (!targetId) {
          const prompt = sourceText;
          const { appName, appDescription, appType } = buildInitialPromptPayload(prompt);
          const createResponse = await fetch(`${API_URL}/api/sitebrain/projects`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({
              name: appName,
              description: appDescription,
              tech_preferences: '',
              app_type: appType,
              full_stack: useFullStack,
            }),
          });

          if (!createResponse.ok) {
            throw new Error('Failed to create project');
          }

          const created = await createResponse.json();
          targetId = created.id;
          setProjectId(created.id);
          setProject({ ...created, files: {}, preview_html: '', preview_version: 0, metadata: { logs: [] } });
        }

        const response = await fetch(`${API_URL}/api/sitebrain/projects/${targetId}/chat`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            message: payloadMessage,
            model: selectedModel,
            auto_apply: !usePlanFirst,
            plan_first: usePlanFirst,
            full_stack: useFullStack,
            autonomous: builderPrefs.autonomous,
            fast_mvp: builderPrefs.fastMvp,
          }),
        });
        if (!response.ok) {
          const { data: payload } = await parseResponseJson(response);
          throw new Error(formatApiDetail(payload, 'Unable to draft a plan'));
        }
        const chatResult = await response.json();
        setChatPending(false);
        const keepsGenerating = ['auto_apply', 'clarified_build', 'initial_build', 'full_rebuild'].includes(chatResult?.mode)
          || chatResult?.status === 'generating'
          || chatResult?.status === 'applying';
        if (keepsGenerating) {
          setIsGenerating(true);
        }
        if (chatResult?.mode === 'section_plan_first' || chatResult?.mode === 'section_plan_rebuild') {
          setIsGenerating(false);
          toast.success('Section plan ready — pick an option in chat');
        } else if (chatResult?.mode === 'clarified_build') {
          toast.success('Continuing your build with your answers…');
        } else if (chatResult?.mode === 'plan_first') {
          setIsGenerating(false);
          toast.success('Drafting modification plan…');
        } else if (chatResult?.mode === 'auto_apply' && isFollowUpChange) {
          toast.success('Applying your changes autonomously…');
        } else if (isFollowUpChange) {
          toast.success('Working on your changes');
        } else if (chatResult?.status === 'generating') {
          toast.success(projectId ? 'Building your app' : 'Starting your build');
        } else if (!keepsGenerating) {
          setIsGenerating(false);
          toast.success(projectId ? 'Building your app' : 'Starting your build');
        }
      }
    } catch (error) {
      setIsGenerating(false);
      setChatPending(false);
      toast.error(error.message || 'Unable to draft a plan');
    }
  };

  const applyModificationPlan = async (planId) => {
    if (!projectId || !planId) return;
    try {
      setApplyingPlan(true);
      setIsGenerating(true);
      const response = await fetch(`${API_URL}/api/sitebrain/projects/${projectId}/apply-plan`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ plan_id: planId, model: selectedModel }),
      });
      if (!response.ok) {
        const { data: payload } = await parseResponseJson(response);
        throw new Error(formatApiDetail(payload, 'Failed to apply plan'));
      }
      setProject((prev) => (prev ? {
        ...prev,
        metadata: { ...(prev.metadata || {}), pending_plan: null },
      } : prev));
      toast.success(
        pendingPlan?.type === 'section_plan'
          ? 'Building from your approved section plan…'
          : 'Applying modification plan…',
      );
    } catch (error) {
      setApplyingPlan(false);
      setIsGenerating(false);
      toast.error(error.message || 'Failed to apply plan');
    }
  };

  const dismissModificationPlan = () => {
    setProject((prev) => (prev ? {
      ...prev,
      metadata: { ...(prev.metadata || {}), pending_plan: null },
    } : prev));
  };

  const handlePlanOption = (planId, option) => {
    const normalized = String(option || '').trim();
    const applyOptions = new Set(['Build website', 'Apply changes', 'Apply plan']);
    const cancelOptions = new Set(['Cancel this plan', 'Dismiss plan']);
    if (applyOptions.has(normalized)) {
      applyModificationPlan(planId || pendingPlan?.id);
      return;
    }
    if (cancelOptions.has(normalized)) {
      dismissModificationPlan();
      return;
    }
    setChatInput(normalized);
  };

  const triggerDeploy = async (target) => {
    if (!projectId) {
      toast.error('Create a project first');
      return;
    }
    try {
      setDeployPending(true);
      if (usePydanticAi) {
        await paiDeployProject({ projectId, target });
      } else {
        const response = await fetch(`${API_URL}/api/sitebrain/projects/${projectId}/deploy`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ target }),
        });
        if (!response.ok) {
          const { data: payload } = await parseResponseJson(response);
          throw new Error(formatApiDetail(payload, 'Failed to trigger deployment'));
        }
      }
      setIsGenerating(true);
      toast.success(`Starting ${target} deployment flow`);
    } catch (error) {
      setDeployPending(false);
      toast.error(error.message || 'Failed to trigger deployment');
    }
  };

  const headerExtras = (
    <>
      {builderPrefs.autonomous ? (
        <Badge
          variant="outline"
          className="hidden border-violet-500/30 bg-violet-500/10 text-[10px] text-violet-200 sm:inline-flex"
          data-testid="app-builder-autonomous-badge"
        >
          Autonomous{builderPrefs.fullStack ? ' · Full-stack' : ''}{builderPrefs.fastMvp ? ' · Fast MVP' : ''}
        </Badge>
      ) : null}
      <Badge
        variant="outline"
        className={`hidden cursor-pointer text-[10px] sm:inline-flex ${usePydanticAi ? 'border-blue-500/30 bg-blue-500/10 text-blue-200' : 'border-white/[0.08] bg-white/[0.03] text-zinc-500'}`}
        onClick={() => setUsePydanticAi(!usePydanticAi)}
        title={usePydanticAi ? 'Using PydanticAI Agent (click to switch to Classic)' : 'Using Classic SiteBrain (click to switch to PydanticAI)'}
        data-testid="app-builder-pydantic-ai-toggle"
      >
        {usePydanticAi ? 'PydanticAI' : 'Classic'}
      </Badge>
      <Badge
        variant="outline"
        className="hidden border-white/[0.08] bg-white/[0.03] text-[10px] text-zinc-400 sm:inline-flex"
        data-testid="app-builder-header-status-chip"
      >
        <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-emerald-400" />
        {projectStateLabel || 'Idle'}
      </Badge>
      <AppBuilderModelPicker
        cloudKeys={cloudKeys}
        value={selectedModel}
        onChange={setSelectedModel}
        defaultFromSettings={settingsDefaultModel}
      />
      <ProjectSelector
        projects={projects}
        activeProjectId={projectId}
        activeProject={project}
        onSelect={(id) => loadProject(id)}
        onDelete={deleteProject}
        onNewProject={createFreshProject}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={downloadProject}
        disabled={!project?.id}
        className="h-8 border-white/[0.08] bg-white/[0.03] text-xs text-zinc-300 hover:bg-white/[0.06] disabled:opacity-50"
        data-testid="app-builder-download-project-button"
      >
        <Download className="mr-1.5 h-3.5 w-3.5" />
        Download
      </Button>
      {agentRunning ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={stopBuild}
          className="h-8 border-red-500/40 bg-red-500/10 text-xs text-red-200 hover:bg-red-500/20"
          data-testid="app-builder-header-stop-button"
        >
          <Square className="mr-1.5 h-3.5 w-3.5" />
          Stop
        </Button>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={publishShareLink}
        disabled={sharePending || !project?.id || !previewHtml}
        className="h-8 border-white/[0.08] bg-white/[0.03] text-xs text-zinc-300 hover:bg-white/[0.06] disabled:opacity-50"
        data-testid="app-builder-publish-share-button"
      >
        {sharePending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Share2 className="mr-1.5 h-3.5 w-3.5" />}
        Share
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={deployPending || !project?.id}
            className="h-8 border-white/[0.08] bg-white/[0.03] text-xs text-zinc-300 hover:bg-white/[0.06] disabled:opacity-50"
            data-testid="app-builder-deploy-menu-trigger"
          >
            Deploy
            <ChevronDown className="ml-1 h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-white/[0.08] bg-[#141414] text-zinc-200">
          <DropdownMenuLabel className="text-xs text-zinc-500">Push to platform</DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-white/[0.06]" />
          {['unified', 'all', 'github', 'gitlab', 'netlify', 'railway'].map((target) => (
            <DropdownMenuItem
              key={target}
              onClick={() => triggerDeploy(target)}
              className="text-xs capitalize focus:bg-white/[0.06] focus:text-white"
              data-testid={`app-builder-deploy-${target}-button`}
            >
              {target === 'unified' ? 'Unified deploy (app + API + DB)' : target === 'all' ? 'All platforms' : target}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  const activeSecondaryView = SECONDARY_VIEWS.find((v) => v.value === rightTab);

  return (
    <DashboardShell
      user={user}
      title={labConfig?.title ?? 'App Builder'}
      subtitle={labConfig?.subtitle ?? 'Describe what you want — we build it for you.'}
      activeNav={labConfig ? 'websites' : 'app-builder'}
      headerExtras={headerExtras}
      fullscreen
      backPath={labConfig ? '/websites' : '/dashboard'}
    >
      <div
        className="flex h-full min-h-0 flex-col bg-[#080808]"
        data-testid={labConfig?.testId ?? 'app-builder-body'}
      >
        {(agentRunning || buildProgress?.completedSteps?.length > 0 || buildProgress?.failedSteps?.length > 0 || pipelinePhases.length > 0) ? (
          <div className="sticky top-0 z-20 shrink-0 border-b border-white/[0.06] bg-[#080808]/95 px-4 py-2 backdrop-blur" data-testid="app-builder-top-progress-bar">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <BuildStepTracker
                    steps={APP_BUILD_STEPS}
                    currentStepId={buildProgress?.currentStepId}
                    completedSteps={buildProgress?.completedSteps || []}
                    failedSteps={buildProgress?.failedSteps || []}
                    compact
                  />
                </div>
                {agentRunning ? (
                  <Button
                    type="button"
                    onClick={stopBuild}
                    size="sm"
                    variant="outline"
                    className="h-8 shrink-0 rounded-full border-red-500/40 bg-red-500/10 px-3 text-red-300 hover:bg-red-500/20"
                    data-testid="app-builder-top-progress-stop-button"
                  >
                    <X className="mr-1.5 h-3.5 w-3.5" />
                    Stop build
                  </Button>
                ) : null}
              </div>
              {/* Pipeline agent tracker removed — progress shown via BuildStepTracker above */}
            </div>
          </div>
        ) : null}
        <PanelGroup autoSaveId={labConfig?.panelSaveId ?? 'snagcart-dyad-builder'} direction="horizontal" className="min-h-0 flex-1">
          <Panel defaultSize={36} minSize={26} maxSize={50} className="min-h-0">
            <aside className="flex h-full min-h-0 flex-col border-r border-white/[0.06] bg-[#0d0d0d]" data-testid="app-builder-sidebar-left">
              <div className="min-h-0 flex-1 overflow-hidden p-3" data-testid="app-builder-sidebar-center">
                <PagesBrandPanel
                  files={files}
                  projectName={project?.name}
                  selectedFile={selectedFile}
                  onSelect={(f) => { setSelectedFile(f); setRightTab('files'); }}
                />
                <SimpleComposePanel
                  project={project}
                  projectId={projectId}
                  chatMessages={chatMessages}
                  chatInput={chatInput}
                  onChatInputChange={setChatInput}
                  onRequestPlan={requestPlan}
                  onSuggestPrompt={setChatInput}
                  onStartTemplate={(prompt) => requestPlan(prompt)}
                  onStop={stopBuild}
                  promptQueue={promptQueue}
                  chatPending={chatPending}
                  running={agentRunning}
                  backgroundFinishing={backgroundFinishing}
                  previewHtml={previewHtml}
                  chatScrollRef={chatScrollRef}
                  buildProgress={buildProgress}
                  pendingPlan={pendingPlan}
                  applyingPlan={applyingPlan}
                  onApplyPlan={applyModificationPlan}
                  onDismissPlan={dismissModificationPlan}
                  onPlanOption={handlePlanOption}
                  requirementsQuestions={requirementsQuestions}
                  requirementsCountdown={requirementsCountdown}
                  requirementsAnswering={requirementsAnswering}
                  onAnswerRequirement={answerRequirement}
                  onSealRequirements={sealRequirements}
                  activeAgent={derivedActiveAgent}
                />
              </div>
            </aside>
          </Panel>

          <PanelResizeHandle className="w-1 bg-white/[0.06] transition-colors hover:bg-violet-500/50" />

          <Panel minSize={45} className="min-h-0">
          <section className="flex h-full min-h-0 flex-col bg-[#080808]" data-testid="app-builder-main-pane">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/[0.06] px-4 py-2.5">
              <div className="flex min-w-0 items-center gap-2">
                {rightTab !== 'preview' ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setRightTab('preview')}
                    className="h-8 text-xs text-zinc-400 hover:text-white"
                    data-testid="app-builder-back-to-preview"
                  >
                    <Eye className="mr-1.5 h-3.5 w-3.5" />
                    Preview
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPreviewSubTab('app')}
                      className={cn(
                        'flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition',
                        previewSubTab === 'app'
                          ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-200'
                          : 'border-white/[0.06] bg-white/[0.03] text-zinc-500 hover:text-zinc-300',
                      )}
                      data-testid="app-builder-tab-preview"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      Live preview
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewSubTab('agent-browser')}
                      className={cn(
                        'flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition',
                        previewSubTab === 'agent-browser'
                          ? 'border-cyan-500/20 bg-cyan-500/10 text-cyan-200'
                          : 'border-white/[0.06] bg-white/[0.03] text-zinc-500 hover:text-zinc-300',
                      )}
                      data-testid="app-builder-tab-agent-browser"
                    >
                      <Monitor className="h-3.5 w-3.5" />
                      Agent browser
                      {browserSession.active ? (
                        <span className="ml-1 h-1.5 w-1.5 rounded-full bg-cyan-400" />
                      ) : null}
                    </button>
                  </div>
                )}
                {rightTab !== 'preview' && activeSecondaryView ? (
                  <span className="truncate text-xs text-zinc-500">{activeSecondaryView.label}</span>
                ) : null}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {engineBadgeLabel ? (
                  <Badge
                    variant="outline"
                    className="border-blue-500/25 bg-blue-500/10 text-[10px] uppercase tracking-wider text-blue-200"
                    data-testid="app-builder-engine-badge"
                  >
                    {engineBadgeLabel}
                  </Badge>
                ) : null}
                <Badge
                  variant="outline"
                  className="border-emerald-500/25 bg-emerald-500/10 text-[10px] text-emerald-200"
                  data-testid="app-builder-preview-status-badge"
                >
                  {previewStatusLabel}
                </Badge>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={refreshPreviewNow}
                  className={cn(
                    'h-8 border-white/[0.08] bg-white/[0.03] text-xs text-zinc-300',
                    previewRefreshPulse && 'border-violet-500/40',
                  )}
                  data-testid="app-builder-refresh-preview-button"
                >
                  <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', previewRefreshPulse && 'animate-spin')} />
                  Refresh
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 border-white/[0.08] bg-white/[0.03] text-xs text-zinc-300"
                      data-testid="app-builder-more-views-trigger"
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="border-white/[0.08] bg-[#141414] text-zinc-200">
                    <DropdownMenuLabel className="text-xs text-zinc-500">More views</DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-white/[0.06]" />
                    {SECONDARY_VIEWS.map(({ value, label, icon: Icon }) => (
                      <DropdownMenuItem
                        key={value}
                        onClick={() => setRightTab(value)}
                        className={cn(
                          'text-xs focus:bg-white/[0.06] focus:text-white',
                          rightTab === value && 'bg-white/[0.04] text-white',
                        )}
                        data-testid={`app-builder-tab-${value}`}
                      >
                        <Icon className="mr-2 h-3.5 w-3.5" />
                        {label}
                      </DropdownMenuItem>
                    ))}
              {generatedAppUrls ? (
                <div className="mb-3 rounded-2xl border border-cyan-500/20 bg-cyan-500/10 p-3" data-testid="generated-app-url-panel">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-200">Generated app URLs</p>
                      <p className="text-xs text-zinc-400">
                        {generatedAppUrls.status === 'live_sandbox' ? 'Live backend sandbox available' : 'Backend contract ready · sandbox pending'}
                      </p>
                    </div>
                    <Badge variant="outline" className="border-emerald-500/25 bg-emerald-500/10 text-[10px] text-emerald-200" data-testid="generated-app-wiring-status">
                      {project?.metadata?.wiring_validation?.status || 'validating'}
                    </Badge>
                  </div>
                  <div className="grid gap-2 md:grid-cols-2">
                    {[
                      ['Frontend', generatedAppUrls.frontend],
                      ['Backend', generatedAppUrls.backend],
                      ['Swagger', generatedAppUrls.swagger],
                      ['Health', generatedAppUrls.health],
                    ].map(([label, value]) => (
                      <div key={label} className="flex min-w-0 items-center gap-2 rounded-xl border border-white/[0.08] bg-black/20 px-3 py-2" data-testid={`generated-url-${label.toLowerCase()}`}>
                        <a href={value} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-xs text-cyan-100 hover:underline">
                          <span className="font-semibold text-white">{label}:</span> {value}
                        </a>
                        <button type="button" onClick={() => copyGeneratedUrl(label, value)} className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-white/10 text-zinc-300 hover:bg-white/10" data-testid={`copy-generated-url-${label.toLowerCase()}`}>
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden px-4 py-4" data-testid="app-builder-right-tabs">
              {shareUrl ? (
                <div className="mb-3 flex items-center gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3 py-2" data-testid="app-builder-share-link-banner">
                  <Share2 className="h-4 w-4 shrink-0 text-emerald-300" />
                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="min-w-0 flex-1 truncate text-xs text-emerald-100 underline-offset-2 hover:underline"
                    data-testid="app-builder-share-link-anchor"
                  >
                    {shareUrl}
                  </a>
                  <button
                    type="button"
                    onClick={copyShareLink}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-emerald-400/20 text-emerald-200 transition hover:bg-emerald-400/10"
                    data-testid="app-builder-copy-share-link-button"
                    title="Copy share link"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : null}
              {rightTab === 'activity' ? <div className="h-full" data-testid="app-builder-terminal-dock"><LogsPanel logs={logs} running={isGenerating} docked /></div> : null}
              {rightTab === 'files' ? <SimpleFilesPanel files={files} selectedFile={activeSelectedFile} onSelectFile={setSelectedFile} fileContent={selectedFileContent} fileName={activeSelectedFile} /> : null}
              {rightTab === 'preview' && previewSubTab === 'agent-browser' ? (
                <BrowserQAPanel
                  steps={browserSession.steps}
                  screenshot={browserSession.screenshot}
                  url={browserSession.url}
                  active={browserSession.active}
                  running={isGenerating}
                />
              ) : null}
              {rightTab === 'preview' && previewSubTab !== 'agent-browser' ? (
                <PreviewPane
                  key={projectId || 'none'}
                  projectId={projectId}
                  previewHtml={previewHtml}
                  previewVersion={previewVersion}
                  files={files}
                  projectName={projectDisplayName}
                  vitePreviewUrl={previewUrl}
                  projectState={project?.state || 'idle'}
                  isGenerating={isGenerating}
                  isLoadingProject={projectLoading}
                  buildStepId={buildProgress?.currentStepId}
                  appType={projectAppType}
                  autoRefreshToken={`${projectId || 'none'}-${previewVersion}-${rightTab}`}
                  onRefresh={refreshPreviewNow}
                  onEnsurePreview={ensurePreviewServer}
                />
              ) : null}
              {rightTab === 'settings' ? (
                <AppBuilderSettingsPanel settingsHook={builderSettings} />
              ) : null}
            </div>
          </section>
          </Panel>
        </PanelGroup>
      </div>
    </DashboardShell>
  );
}

const TEMPLATE_START_OPTIONS = [
  {
    id: 'coffee',
    label: 'Coffee shop',
    emoji: '☕',
    prompt:
      'Create a premium hospitality landing page (Page UI / editorial quality): warm palette, glass nav, hero with lifestyle imagery, menu highlights bento grid, hours & location, testimonials, and contact form. Use framer-motion scroll reveals and shadcn Card/Button.',
  },
  {
    id: 'restaurant',
    label: 'Restaurant',
    emoji: '🍽️',
    prompt:
      'Create a premium restaurant website (Page UI quality): cinematic hero, featured dishes bento grid, reservations CTA, chef story, hours, map section, testimonials, and contact form. Animated sections with framer-motion.',
  },
  {
    id: 'ecommerce',
    label: 'Online store',
    emoji: '🛒',
    prompt:
      'Create a modern e-commerce storefront (HyperUI / MynaUI quality): sticky glass nav, hero with featured collection, product bento grid, category pills, trust badges, cart UI, and checkout CTA. framer-motion hover on product cards.',
  },
  {
    id: 'saas',
    label: 'SaaS landing',
    emoji: '🚀',
    prompt:
      'Create a premium SaaS landing page matching https://emerge-frontend-production.up.railway.app/ quality: dark glass navbar, Spotlight + BackgroundBeams hero, dashboard mockup with NumberTicker metric bento tiles, Marquee logo strip, stats strip, BentoGrid features, tabbed product modules, 3-tier pricing, testimonials with InfiniteMovingCards, FAQ accordion, ShimmerButton final CTA. Import from @/components/magicui/* and @/components/aceternity/*.',
  },
  {
    id: 'portfolio',
    label: 'Portfolio',
    emoji: '🎨',
    prompt:
      'Create a creative portfolio site (Aceternity / Page UI quality): bold typography hero, project bento gallery with hover lift, about section, skills grid, client logo marquee, and contact form. framer-motion whileInView on every section.',
  },
  {
    id: 'real-estate',
    label: 'Real estate',
    emoji: '🏠',
    prompt:
      'Create a real estate listing site (Origin UI / Page UI quality): search hero, featured property cards grid, agent profile, neighborhood stats strip, testimonials, and lead capture form. Glass cards with backdrop-blur.',
  },
  {
    id: 'fitness',
    label: 'Fitness studio',
    emoji: '💪',
    prompt:
      'Create a fitness studio website (MynaUI / HyperUI quality): energetic hero, class schedule cards, pricing tiers, trainer bios grid, testimonials, and booking CTA. Motion on cards and CTA buttons.',
  },
  {
    id: 'agency',
    label: 'Agency',
    emoji: '✨',
    prompt:
      'Create a digital agency landing page (Magic UI / Aceternity quality): glass nav, gradient mesh hero, services bento grid, case study showcase, process steps, team grid, client marquee, and contact form. Premium dark or light editorial palette.',
  },
];

const FOLLOW_UP_SUGGESTED_PROMPTS = [
  'Implement contact info with phone, email, and address',
  'Save contact form submissions to the database',
  'Send email notification when someone submits the contact form',
  'Add a full-stack backend with leads API',
];

function TemplateStartChips({ options = [], onStart, disabled = false }) {
  if (!options?.length) return null;
  return (
    <div className="mt-4 w-full max-w-sm space-y-2" data-testid="app-builder-template-start-chips">
      <p className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">Start from a template</p>
      <div className="grid grid-cols-2 gap-2">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            disabled={disabled}
            onClick={() => onStart?.(option.prompt)}
            className="flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.03] px-3 py-2.5 text-left text-xs text-zinc-300 transition hover:border-violet-500/40 hover:bg-violet-500/[0.08] hover:text-white disabled:opacity-50"
            data-testid={`app-builder-template-chip-${option.id}`}
          >
            <span className="text-base leading-none" aria-hidden>{option.emoji}</span>
            <span className="font-medium">{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PlanConversationOptions({ planId, options, sections, applying, disabled, onSelect }) {
  if (!options?.length) return null;
  return (
    <div className="mt-3 space-y-3" data-testid="app-builder-plan-conversation-options">
      {sections?.length ? (
        <ol className="max-h-48 space-y-1.5 overflow-y-auto rounded-xl border border-white/10 bg-black/20 px-3 py-2">
          {sections.map((section) => (
            <li key={section.order || section.name} className="text-[11px] leading-5 text-zinc-400">
              <span className="font-semibold text-violet-200">{section.order}. {section.name}</span>
              {section.purpose ? <span className="text-zinc-500"> — {section.purpose}</span> : null}
            </li>
          ))}
        </ol>
      ) : null}
      <div className="flex flex-col gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            disabled={disabled || applying}
            onClick={() => onSelect?.(planId, option)}
            className={cn(
              'rounded-xl border px-3 py-2.5 text-left text-xs transition disabled:opacity-50',
              option === 'Build website' || option === 'Apply changes'
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100 hover:border-emerald-400/40 hover:bg-emerald-500/15'
                : 'border-violet-500/20 bg-violet-500/[0.06] text-violet-100/90 hover:border-violet-400/35 hover:bg-violet-500/[0.1]',
            )}
            data-testid="app-builder-plan-option-chip"
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

function RequirementsChips({
  questions,
  countdown,
  answering,
  projectId,
  onAnswer,
  onSeal,
}) {
  if (!questions?.length) return null;
  return (
    <div className="space-y-3 rounded-xl border border-violet-500/20 bg-violet-500/[0.06] p-3" data-testid="app-builder-requirements-chips">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-violet-200">Quick choices</p>
        {countdown != null ? (
          <span className="text-[10px] text-zinc-500" data-testid="app-builder-requirements-countdown">
            Auto-continue in {countdown}s
          </span>
        ) : null}
      </div>
      {questions.slice(0, 3).map((q) => (
        <div key={q.question_id || q.slot_id} className="space-y-2">
          <p className="text-xs text-zinc-300">{q.question}</p>
          <div className="flex flex-wrap gap-2">
            {(q.options || []).slice(0, 4).map((opt) => (
              <button
                key={`${q.slot_id}-${opt}`}
                type="button"
                disabled={answering}
                onClick={() => onAnswer?.(projectId, q.slot_id, opt, q.question_id)}
                className={cn(
                  'rounded-lg border px-2.5 py-1.5 text-xs transition',
                  opt === q.default_selected
                    ? 'border-violet-500/40 bg-violet-600/20 text-violet-100'
                    : 'border-white/[0.08] bg-white/[0.03] text-zinc-300 hover:border-violet-500/30 hover:text-white',
                  answering && 'opacity-50',
                )}
                data-testid={`app-builder-requirement-chip-${q.slot_id}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        disabled={answering}
        onClick={() => onSeal?.(projectId)}
        className="text-[10px] text-zinc-500 underline-offset-2 hover:text-zinc-300 hover:underline"
        data-testid="app-builder-requirements-seal-now"
      >
        Continue with defaults now
      </button>
    </div>
  );
}

function SimpleComposePanel({
  project,
  projectId,
  chatMessages,
  chatInput,
  onChatInputChange,
  onRequestPlan,
  onSuggestPrompt,
  onStartTemplate,
  onStop,
  promptQueue = [],
  chatPending,
  running,
  backgroundFinishing,
  previewHtml,
  chatScrollRef,
  buildProgress,
  pendingPlan,
  applyingPlan,
  onApplyPlan,
  onDismissPlan,
  onPlanOption,
  requirementsQuestions,
  requirementsCountdown,
  requirementsAnswering,
  onAnswerRequirement,
  onSealRequirements,
  activeAgent,
}) {
  useEffect(() => {
    const root = chatScrollRef?.current;
    if (!root) return;
    const viewport = root.querySelector('[data-radix-scroll-area-viewport]') || root;
    viewport.scrollTop = viewport.scrollHeight;
  }, [chatMessages.length, chatPending, running, chatScrollRef, buildProgress]);

  const statusLine = backgroundFinishing
    ? 'Preview is live — agent is finishing QA in the background'
    : running
      ? (activeAgent?.title ? `${activeAgent.title} is working…` : 'Agent is building your app…')
      : null;

  return (
    <Card
      className="flex h-full min-h-0 flex-col border-white/[0.06] bg-white/[0.02] shadow-none"
      data-testid="app-builder-chat-panel"
    >
      <CardHeader className="shrink-0 flex-row items-center justify-between space-y-0 border-b border-white/[0.06] px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600">
            <Wand2 className="h-4 w-4 text-white" />
          </div>
          <div>
            <CardTitle className="text-sm font-medium text-zinc-100">Chat</CardTitle>
            {statusLine ? (
              <p className="text-xs text-emerald-400/80">{statusLine}</p>
            ) : (
              <p className="text-xs text-zinc-500">Describe changes or start a new build</p>
            )}
          </div>
        </div>
        {running ? <Loader2 className="h-4 w-4 animate-spin text-emerald-400" /> : null}
      </CardHeader>
      <ActiveAgentBar agent={activeAgent} running={running} />
      <ScrollArea ref={chatScrollRef} className="min-h-0 flex-1">
        <div
          className="space-y-3 px-4 py-4"
          data-testid="app-builder-chat-message-list"
        >
          {!chatMessages.length ? (
            <div className="flex min-h-[200px] flex-col items-center justify-center px-2 text-center" data-testid="app-builder-chat-empty-state">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/[0.08] bg-white/[0.03]">
                <Wand2 className="h-5 w-5 text-violet-400" />
              </div>
              <p className="text-sm font-medium text-zinc-200">
                {project?.id ? 'What should we change?' : 'What do you want to build?'}
              </p>
              <p className="mt-1 max-w-xs text-xs leading-5 text-zinc-500">
                {project?.id
                  ? 'Try “add a pricing section”, “save contact submissions”, or “send email on form submit”.'
                  : 'Describe your app in the chat — preview updates as the build runs.'}
              </p>
              {project?.id ? (
                <div className="mt-4 flex w-full max-w-sm flex-col gap-2">
                  {FOLLOW_UP_SUGGESTED_PROMPTS.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      onClick={() => onSuggestPrompt?.(prompt)}
                      className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5 text-left text-xs text-zinc-400 transition hover:border-violet-500/30 hover:bg-violet-500/[0.06] hover:text-zinc-200"
                      data-testid="app-builder-followup-suggested-prompt"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <>
              {chatMessages.map((message, idx) => (
                <ChatBubble
                  key={message.id}
                  message={message}
                  running={running && idx === chatMessages.length - 1 && message.role === 'assistant'}
                  chatPending={chatPending}
                  applyingPlan={applyingPlan}
                  onPlanOption={onPlanOption}
                />
              ))}
              {pendingPlan?.status === 'awaiting_confirmation'
                && !chatMessages.some((m) => m.plan_id === pendingPlan.id && m.plan_options?.length)
                ? (
                  <PlanConversationOptions
                    planId={pendingPlan.id}
                    options={
                      pendingPlan.type === 'section_plan'
                        ? ['Build website', 'Add another section', 'Use a lighter visual style', 'Revise the plan in chat']
                        : ['Apply changes', 'Revise the plan in chat', 'Cancel this plan']
                    }
                    sections={(pendingPlan.sections || []).map((section) => ({
                      order: section.order,
                      name: section.name,
                      purpose: section.purpose,
                    }))}
                    applying={applyingPlan}
                    disabled={chatPending || running}
                    onSelect={onPlanOption}
                  />
                ) : null}
              <RequirementsChips
                questions={requirementsQuestions}
                countdown={requirementsCountdown}
                answering={requirementsAnswering}
                projectId={projectId}
                onAnswer={onAnswerRequirement}
                onSeal={onSealRequirements}
              />
            </>
          )}
        </div>
      </ScrollArea>

      <CardFooter className="shrink-0 border-t border-white/[0.06] p-3">
        <div className="w-full rounded-xl border border-white/[0.08] bg-white/[0.03] p-2">
          {promptQueue.length > 0 ? (
            <div className="mb-1 flex items-center gap-1.5 px-1 text-[11px] text-amber-300" data-testid="app-builder-queue-badge">
              <Loader2 className="h-3 w-3 animate-spin" />
              {promptQueue.length} prompt{promptQueue.length === 1 ? '' : 's'} queued
            </div>
          ) : null}
          <Textarea
            value={chatInput}
            onChange={(event) => onChatInputChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && chatInput.trim() && !chatPending) {
                event.preventDefault();
                onRequestPlan();
              }
            }}
            placeholder={
              project?.state === 'awaiting_clarification'
                ? 'Answer the questions above to continue your build…'
                : project?.state === 'awaiting_requirements'
                  ? 'Or type a custom answer…'
                  : backgroundFinishing
                    ? 'Preview is live — request the next change'
                    : running
                      ? 'Building… type to queue your next prompt'
                      : project?.id
                        ? 'Ask for changes…'
                        : 'Describe your app…'
            }
            rows={3}
            className="min-h-[72px] resize-none border-0 bg-transparent px-2 py-1.5 text-sm leading-6 text-white shadow-none placeholder:text-zinc-600 focus-visible:ring-0"
            disabled={chatPending}
            data-testid="app-builder-chat-input"
          />
          <div className="flex items-center justify-between gap-2 px-1 pt-1">
            <span className="text-[10px] text-zinc-600" data-testid="app-builder-chat-count-badge">
              {chatMessages.length} message{chatMessages.length === 1 ? '' : 's'}
            </span>
            <div className="flex items-center gap-2">
              {running ? (
                <Button
                  type="button"
                  onClick={onStop}
                  size="sm"
                  variant="outline"
                  className="h-9 rounded-xl border-red-500/40 bg-red-500/10 px-3 text-red-300 hover:bg-red-500/20"
                  data-testid="app-builder-stop-button"
                >
                  <Square className="mr-1.5 h-3.5 w-3.5" />
                  Stop
                </Button>
              ) : null}
              <Button
                type="button"
                onClick={() => onRequestPlan()}
                disabled={chatPending || !chatInput.trim()}
                size="sm"
                className="h-9 rounded-xl bg-violet-600 px-4 text-white hover:bg-violet-500 disabled:opacity-40"
                data-testid="app-builder-request-plan-button"
              >
                {chatPending ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                )}
                {running ? 'Queue' : backgroundFinishing || project?.preview_html || previewHtml ? 'Send' : project?.state === 'awaiting_clarification' ? 'Continue build' : 'Start build'}
              </Button>
            </div>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}

function ChatBubble({ message, running, chatPending = false, applyingPlan = false, onPlanOption }) {
  const isUser = message.role === 'user';
  const isActivity = message.role === 'activity';
  const isEdit = message.role === 'edit';
  const meta = chatMessageMeta(message);
  const displayContent = isUser || isActivity || isEdit ? message.content : sanitizeChatContent(message.content);
  const label = isEdit ? (message.label || 'Auto Fix') : meta.label;
  const labelClass = isEdit ? 'text-sky-300' : meta.toneClass;
  const isThinking = isActivity && (message.kind === 'thinking' || /^thinking$/i.test((displayContent || '').split('\n')[0]?.trim()));
  const thinking = isThinking ? parseThinkingContent(displayContent) : null;
  const progressPct = message.progress_pct ?? thinking?.progressPct ?? null;
  const bodyText = thinking ? thinking.body : displayContent;
  const headerText = thinking ? thinking.header : null;
  const fixingText = message.fixing || thinking?.fixing || '';
  const nextPlanText = message.next_plan || thinking?.nextPlan || '';

  return (
    <div className={cn('flex gap-2.5', isUser ? 'flex-row-reverse' : 'flex-row')} data-testid={`app-builder-chat-bubble-${message.id}`}>
      <Avatar className="h-7 w-7 shrink-0">
        <AvatarFallback
          className={cn(
            'text-[10px] font-semibold',
            isUser ? 'bg-violet-600/30 text-violet-200' : isEdit ? 'bg-sky-500/20 text-sky-200' : isActivity ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/[0.08] text-zinc-400',
          )}
        >
          {isUser ? 'You' : isEdit ? 'Fx' : isActivity ? (label?.[0] || '·') : 'AI'}
        </AvatarFallback>
      </Avatar>
      <Card
        className={cn(
          'max-w-[85%] border-0 shadow-none',
          isUser
            ? 'bg-violet-600/15'
            : isEdit
              ? 'border border-sky-500/20 bg-sky-500/5'
              : isActivity
                ? cn(
                  'border bg-emerald-500/[0.04]',
                  isThinking ? 'border-violet-500/20 bg-violet-500/[0.05]' : 'border-emerald-500/15',
                )
                : 'bg-white/[0.04]',
        )}
      >
        <CardContent
          className={cn(
            'p-3',
            isUser ? 'text-zinc-100' : isEdit ? 'text-sky-100/90' : isActivity ? 'text-zinc-300' : 'text-zinc-200',
          )}
        >
          <div className="mb-1 flex items-center justify-between gap-2">
            {!isUser ? (
              <p className={cn('font-[\'JetBrains_Mono\',monospace] text-[10px] uppercase tracking-[0.14em]', labelClass)} data-testid={`app-builder-chat-label-${message.id}`}>
                {label}
              </p>
            ) : (
              <span />
            )}
            <p className="shrink-0 text-[10px] text-zinc-600" data-testid={`app-builder-chat-time-${message.id}`}>
              {formatChatTimestamp(message.timestamp)}
            </p>
          </div>
          {headerText ? (
            <p className="mb-1 font-[\'JetBrains_Mono\',monospace] text-[11px] font-medium uppercase tracking-[0.12em] text-violet-300">
              {headerText}
            </p>
          ) : null}
          <p className={cn('whitespace-pre-wrap text-sm leading-6', (isActivity || isEdit) && 'text-xs text-zinc-300')} data-testid={`app-builder-chat-text-${message.id}`}>
            {bodyText}
          </p>
          {message.mode === 'plan_ready' || message.plan_options?.length ? (
            <PlanConversationOptions
              planId={message.plan_id}
              options={message.plan_options}
              sections={message.plan_sections}
              applying={applyingPlan}
              disabled={chatPending || running}
              onSelect={onPlanOption}
            />
          ) : null}
          {fixingText ? (
            <p className="mt-2 text-xs leading-5 text-amber-200/90">
              <span className="font-medium text-amber-300">Fixing: </span>
              {fixingText}
            </p>
          ) : null}
          {nextPlanText ? (
            <p className="mt-1 text-xs leading-5 text-zinc-400">
              <span className="font-medium text-zinc-300">Next plan: </span>
              {nextPlanText}
            </p>
          ) : null}
          {isEdit && message.path ? (
            <p className="mt-1 font-['JetBrains_Mono',monospace] text-[10px] text-sky-400/80">{message.path}</p>
          ) : null}
          {typeof progressPct === 'number' ? (
            <div className="mt-2 space-y-1" data-testid={`app-builder-chat-progress-${message.id}`}>
              <div className="flex items-center justify-between text-[10px] text-zinc-500">
                <span>Progress</span>
                <span>~{progressPct}%</span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-violet-500/80 transition-all" style={{ width: `${Math.max(0, Math.min(100, progressPct))}%` }} />
              </div>
            </div>
          ) : null}
          {isEdit && message.diff ? (
            <pre className="mt-2 max-h-40 overflow-auto rounded-lg border border-white/10 bg-black/40 p-2 font-['JetBrains_Mono',monospace] text-[10px] leading-5 text-zinc-400">
              {message.diff}
            </pre>
          ) : null}
          {running && !isUser && !isActivity && !isEdit ? (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-zinc-500">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Working…</span>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

function LogsPanel({ logs, running, docked = false }) {
  return (
    <div className={cn('flex h-full flex-col overflow-hidden rounded-2xl bg-[#080808]', docked ? 'border border-white/10' : 'border border-white/10')}>
      <div className="flex min-h-0 flex-1 flex-col p-0">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div>
            <p className="font-['JetBrains_Mono',monospace] text-[11px] text-zinc-500">Activity</p>
            <h3 className="mt-1 flex items-center gap-2 font-['IBM_Plex_Sans',sans-serif] text-base font-medium text-white xl:text-lg">
              <Terminal className="h-4 w-4 text-[#00FF41]" />
              {running ? 'Working on your project…' : 'Ready for your next request'}
            </h3>
          </div>
          <Badge className="border border-white/10 bg-black px-2 py-1 font-['JetBrains_Mono',monospace] text-[10px] text-zinc-200" data-testid="app-builder-log-count-badge">
            {logs.length} lines
          </Badge>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 font-['JetBrains_Mono',monospace] text-[12px] leading-6 text-emerald-200" data-testid="app-builder-terminal-log-list">
          {logs.length ? logs.map((line) => (
            <motion.div key={line.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-3 rounded-xl border border-white/5 bg-black px-3 py-3" data-testid={`terminal-line-${line.id}`}>
              <div className="mb-1 flex items-center justify-between gap-3 text-[10px] text-zinc-500">
                <span>{line.kind}</span>
                <span>{new Date(line.timestamp).toLocaleTimeString()}</span>
              </div>
              <div>{line.text}</div>
            </motion.div>
          )) : <div className="flex h-full items-center justify-center text-center text-zinc-500">Send a message in chat and progress will show up here.</div>}
        </div>
      </div>
    </div>
  );
}

function SimpleFilesPanel({ files, selectedFile, onSelectFile, fileContent, fileName }) {
  return (
    <div className="grid h-full min-h-0 grid-cols-[220px_minmax(0,1fr)] gap-3 max-md:grid-cols-1">
      <div className="min-h-0 overflow-hidden rounded-2xl border border-white/10 bg-[#080808]">
        <div className="border-b border-white/10 px-4 py-3 font-['JetBrains_Mono',monospace] text-[11px] text-zinc-500">Files</div>
        <div className="min-h-0 h-[calc(100%-49px)] overflow-y-auto px-2 py-3" data-testid="app-builder-file-tree-panel">
          {Object.keys(files).length ? <FileTree files={files} selectedFile={selectedFile} onSelect={onSelectFile} /> : <div className="px-3 py-4 text-sm text-zinc-500">Files will appear here after the builder starts producing output.</div>}
        </div>
      </div>
      <div className="min-h-0 overflow-hidden rounded-2xl border border-white/10 bg-[#080808]">
        <CodeEditor content={fileContent || '// No file selected yet'} fileName={fileName || 'Choose a file'} copyTestId="files-panel-code-editor-copy-button" />
      </div>
    </div>
  );
}
